"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.YandexMap = void 0;
var _mui = require("@ori-ui/mui");
var _testidGenerator = _interopRequireDefault(require("@ori/testid-generator"));
var _reactYandexMaps = require("@pbe/react-yandex-maps");
var _react = require("react");
var _useClusteredPickupPointsQuery = require("../../api/useClusteredPickupPointsQuery");
var _SearchConfigurationProvider = require("../../containers/SearchConfigurationProvider");
var _contexts = require("../../contexts");
var _models = require("../../models");
var _shared = require("../shared");
var _jsxRuntime = require("react/jsx-runtime");
const MapContainer = (0, _mui.styled)('div')(() => ({
  width: '100%',
  height: '100%'
}));
const transformLatLng = coordinates => ({
  latitude: coordinates[0] ?? 0,
  longitude: coordinates[1] ?? 0
});
const YandexInner = ({
  isMapVisible,
  activeFilter,
  instantSearchTerm,
  selectedDeliveryChannel
}) => {
  const {
    getTestIdProps
  } = (0, _testidGenerator.default)();
  const yMaps = (0, _reactYandexMaps.useYMaps)(['Map', 'Placemark']);
  const map = (0, _react.useRef)();
  const mapContainer = (0, _react.useRef)(null);
  const {
    viewSettings,
    setMapMethods,
    setViewSettings
  } = (0, _SearchConfigurationProvider.useSearchConfiguration)();
  const {
    currentLocation,
    setSelectedDelivery
  } = (0, _contexts.useMap)();
  const getPickupPoints = (0, _useClusteredPickupPointsQuery.useClusteredPickupPointsQuery)(isMapVisible);
  const updatePushpins = (0, _react.useCallback)(async () => {
    if (!yMaps || !map.current) {
      return;
    }
    map.current.geoObjects.removeAll();
    const bounds = map.current.getBounds();
    const data = await getPickupPoints({
      east: bounds[1]?.[1],
      filter: activeFilter,
      north: bounds[1]?.[0],
      searchTerm: instantSearchTerm,
      south: bounds[0]?.[0],
      west: bounds[0]?.[1],
      zoom: map.current.getZoom()
    });
    data.forEach(({
      cluster,
      pickupPoint,
      displayedClusterSize,
      coordinates
    }) => {
      let icon = '';
      let clickHandler;
      if (pickupPoint) {
        icon = (0, _shared.getPickupPointMapIconSvgString)(pickupPoint.deliveryMethod, pickupPoint.deliveryChannelId === selectedDeliveryChannel, true);
        clickHandler = () => setSelectedDelivery(pickupPoint.deliveryChannelId, coordinates, viewSettings.zoom);
      } else {
        const {
          lowerLeft: {
            latitude: south = 0,
            longitude: west = 0
          } = {},
          upperRight: {
            latitude: north = 0,
            longitude: east = 0
          } = {}
        } = cluster ?? {};
        icon = (0, _shared.getClusterMapIconSvgString)(displayedClusterSize, false, true);
        clickHandler = () => {
          const padding = Math.round(_shared.baseMarkerSize / 2);
          void map.current?.setBounds([[south, west], [north, east]], {
            zoomMargin: [padding, padding]
          });
        };
      }
      const placemark = new yMaps.Placemark([coordinates.latitude, coordinates.longitude], {}, {
        iconImageHref: icon,
        iconLayout: 'default#image',
        iconImageSize: [60, 60]
      });
      placemark.events.add('click', clickHandler);
      map.current?.geoObjects.add(placemark);
    });
    if (currentLocation) {
      map.current.geoObjects.add(new yMaps.Placemark([currentLocation.latitude, currentLocation.longitude], {}, {
        iconImageHref: (0, _shared.getCurrentLocationIconSvgString)(true),
        iconLayout: 'default#image',
        iconImageSize: [60, 60]
      }));
    }
  }, [activeFilter, currentLocation, getPickupPoints, instantSearchTerm, selectedDeliveryChannel, setSelectedDelivery, viewSettings.zoom, yMaps]);

  // Load map script
  (0, _react.useEffect)(() => {
    if (!yMaps || !mapContainer.current) {
      return;
    }
    if (!map.current) {
      const yandexMap = new yMaps.Map(mapContainer.current, {
        center: [viewSettings.location.latitude, viewSettings.location.longitude],
        zoom: viewSettings.zoom
      });
      map.current = yandexMap;

      // Deselect last selection
      yandexMap.events.add('click', () => setSelectedDelivery());

      // Update map view settings
      yandexMap.events.add('boundschange', () => {
        const location = transformLatLng(yandexMap.getCenter());
        const zoom = yandexMap.getZoom();
        setViewSettings(p => zoom === p.zoom && location.latitude === p.location.latitude && location.longitude === p.location.longitude ? p : {
          location,
          zoom
        });
      });

      // TODO: Remove map methods after flat selection UI is used in all markets
      const getViewSettings = viewOptions => ({
        location: viewOptions?.location ?? transformLatLng(yandexMap.getCenter()),
        zoom: viewOptions?.zoom ?? yandexMap.getZoom()
      });

      // TODO: Remove map methods after flat selection UI is used in all markets
      // Expose the generic map methods
      setMapMethods({
        getViewSettings,
        setLocationOrZoom: viewOptions => {
          const {
            location,
            zoom
          } = getViewSettings(viewOptions);
          void yandexMap.setCenter([location.latitude, location.longitude], zoom);
          setViewSettings({
            location,
            zoom
          });
        },
        getViewBounds: () => {
          const [sw = [], ne = []] = yandexMap.getBounds();
          const [north = 0, east = 0] = ne;
          const [south = 0, west = 0] = sw;
          return {
            east,
            north,
            south,
            west
          };
        },
        setViewBounds: ({
          east,
          north,
          south,
          west
        }, padding) => {
          void yandexMap.setBounds([[south, west], [north, east]], {
            zoomMargin: padding === undefined ? undefined : [padding, padding]
          });
        }
      });
    }

    // Update map view to match view settings
    void map.current.setCenter([viewSettings.location.latitude, viewSettings.location.longitude], viewSettings.zoom);
    void updatePushpins();
  }, [currentLocation, viewSettings, yMaps, setSelectedDelivery, updatePushpins, setMapMethods, setViewSettings]);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(MapContainer, {
    ref: mapContainer,
    ...getTestIdProps(_models.TestId.YandexMapContainer)
  });
};
const YandexMap = props => {
  const {
    credentials
  } = (0, _SearchConfigurationProvider.useSearchConfiguration)();
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactYandexMaps.YMaps, {
    query: {
      apikey: credentials
    },
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(YandexInner, {
      ...props
    })
  });
};
exports.YandexMap = YandexMap;
var _default = exports.default = YandexMap;